// Subscriptions.js
import React, { useState, useEffect, useMemo } from 'react';
import DataTable_AgGrid from './DataTable_AgGrid';
import { useAuth } from './AuthContext';
import api from './axios';
import './Subscriptions.css';

export default function Subscriptions() {
  const { companyId, logout } = useAuth();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const [showRunPopup, setShowRunPopup] = useState(false);
  const [runData, setRunData]           = useState([]);
  const [runError, setRunError]         = useState('');

  // Haal subscriptions
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post('/api/get_subscriptions', { companyId });
        const rawSubs = Array.isArray(data.subscriptions) ? data.subscriptions : [];
        const subs = rawSubs.map(s => ({
        ...s,
        active: !!s.active    // 1 => true, 0 => false
        }));
        setSubscriptions(subs);
      } catch (err) {
        console.error(err);
        setError('Failed to load subscriptions');
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  // Globale handler voor “Run”‐knop
  useEffect(() => {
    window.handleRunSubscription = async (rowData) => {
      setRunError('');
      setShowRunPopup(true);
      try {
        const resp = await api.post('/api/subscription_get_base_dataset', {
          subscription_id: rowData.id,
          companyId
        });
        const payload = resp.data;
        let rows = [];
        if (Array.isArray(payload)) {
          rows = payload;
        } else if (Array.isArray(payload.results)) {
          rows = payload.results;
        } else if (Array.isArray(payload.data)) {
          rows = payload.data;
        }
        setRunData(rows);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401) {
          await logout();
          window.location.href = `/login/${companyId}`;
        } else {
          setRunError(err.response?.data?.error || 'Network error while running subscription');
        }
      }
    };

    return () => {
      delete window.handleRunSubscription;
    };
  }, [companyId, logout]);

  const columns = useMemo(() => [
    { field: 'id',                    headerName: 'ID', hide: true },
    { field: 'base_company_name',       headerName: 'Base Company', order: 1 },
    { field: 'base_user_email',          headerName: 'Base User Email',    order: 2 },
    {
      field: 'base_dataset_definition',
      headerName: 'Base Dataset',
      order: 3,
      cellRenderer: params => <code style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(params.value, null, 2)}</code>
    },
    { field: 'connected_company_name',  headerName: 'Connected Company', order: 4 },
    { field: 'connected_user_email',     headerName: 'Connected User Email',    order: 5 },
    {
      field: 'mapping_definition',
      headerName: 'Mapping',
      order: 6,
      cellRenderer: params => <code style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(params.value, null, 2)}</code>
    },
    {
      field: 'connected_dataset_definition',
      headerName: 'Connected Dataset',
      order: 7,
      cellRenderer: params => <code style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(params.value, null, 2)}</code>
    },
    {
      field: 'action_definition',
      headerName: 'Action',
      order: 8,
      cellRenderer: params => <code style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(params.value, null, 2)}</code>
    },
    {
      field: 'time_scheme',
      headerName: 'Schedule',
      order: 9,
      cellRenderer: params => <code style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(params.value, null, 2)}</code>
    },
    {
    field: 'active',
    headerName: 'Active',
    order: 10,
    cellRenderer: params => (
        <input
        type="checkbox"
        checked={!!params.value}
        readOnly
        style={{ cursor: 'default' }}
        />
    )
    },
    {
      field: '',
      headerName: 'Run',
      order: 11,
      type: 'button',
      buttontext: 'Run',
      function: 'handleRunSubscription',
      defaultWidth: 100
    }
  ].sort((a, b) => a.order - b.order), []);

  const runColumns = useMemo(() => {
    if (!runData.length) return [];
    return Object.keys(runData[0]).map(key => ({
      field: key,
      headerName: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }));
  }, [runData]);

  if (loading) return <div>Loading subscriptions…</div>;
  if (error)   return <div className="popup">Error: {error}</div>;

  return (
    <>
      <div style={{ height: '80vh', width: '100%' }}>
        <DataTable_AgGrid
          initialData={subscriptions}
          columns={columns}
          isEditable={false}
          suppressNoRowsOverlay={true}
        />
      </div>

      {showRunPopup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Run Result</h3>

            {/* Toon foutmelding in de modal als die er is */}
            {runError && <div className="popup-error">{runError}</div>}

            <div style={{ height: '50vh', width: '100%' }}>
              <DataTable_AgGrid
                initialData={runData}
                columns={runColumns}
                isEditable={false}
                suppressNoRowsOverlay={true}
              />
            </div>

            <button className="run-button" style={{width: '120px'}} onClick={() => setShowRunPopup(false)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}
